import React, { useState, useEffect } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export type TaskPopupMode = 'create' | 'edit' | 'view'

export interface TaskFormData {
  // Basic Info
  taskId: string
  mainTaskId: string
  taskName: string
  taskDescription: string
  // Assignment
  taskTaken: string
  taskGivenTo: string
  taskGivenBy: string
  
  // Status & Tracking
  status: 'Open' | 'In Progress' | 'On Hold' | 'Closed' | 'Completed'
  anyBlocker: string
  worklogHour: number
  startDate: string
  endDate: string
  totalCall?: string | number
  
  // Additional fields
  phone?: string
  openWU?: number
  logNo?: string
  deliveryUnit?: string
  priority?: 'high' | 'medium' | 'low'
  estimatedHours?: number
  actualHours?: number
  tags?: string[]
  initials?: string
  days?: number
}

export interface Task {
  taskId: string
  mainTaskId: string
  task: string
  taskDescription?: string  // Added this field
  taskTaken: string
  taskGivenTo: string
  taskGivenBy: string
  status: 'Open' | 'In Progress' | 'On Hold' | 'Closed' | 'Completed'
  anyBlocker: string
  worklogHour: number
  startDate: string
  endDate: string
  phone?: string
  initials?: string
  openWU?: number
  logNo?: string
  deliveryUnit?: string
  priority?: 'high'|'medium'|'low'
  days?: number
  estimatedHours?: number
  actualHours?: number
  tags?: string[]
  taskGivenToName?: string
  taskGivenByName?: string
  totalCall?: number
  workLogs?: any[]
  comments?: any[]
}

export interface Employee {
  ID: number
  EmployeeID: number
  EmployeeCode: string
  EmployeeName: string
  Email: string
  MobileNumber: string
  Department: string
  Designation: string
  EmploymentStatus: string
  IsActive: boolean
  firstName?: string
  lastName?: string
}

interface TaskPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (task: TaskFormData) => void
  onDelete?: () => void
  task?: Task | null
  mode: TaskPopupMode
  employees?: Employee[] 
  loggedInEmployeeCode: string | null
  isSubmitting?: boolean
  submitError?: string | null
}

// ── Helper Functions for Display Formatting ─────────────────────────────────

const formatTaskIdForDisplay = (taskId: string): string => {
  if (!taskId || taskId === '') return 'TK-0000'
  if (taskId.startsWith('TK-')) return taskId
  const num = parseInt(taskId, 10)
  if (isNaN(num)) return 'TK-0000'
  return `TK-${String(num).padStart(4, '0')}`
}

const parseTaskIdFromDisplay = (displayId: string): string => {
  if (!displayId) return ''
  const num = displayId.replace('TK-', '')
  const parsed = parseInt(num, 10)
  return isNaN(parsed) ? '' : String(parsed)
}

const formatMainTaskIdForDisplay = (mainTaskId: string): string => {
  if (!mainTaskId || mainTaskId === '') return 'MT-0000'
  if (mainTaskId.startsWith('MT-')) return mainTaskId
  const num = parseInt(mainTaskId, 10)
  if (isNaN(num)) return 'MT-0000'
  return `MT-${String(num).padStart(4, '0')}`
}

const parseMainTaskIdFromDisplay = (displayId: string): string => {
  if (!displayId) return ''
  const num = displayId.replace('MT-', '')
  const parsed = parseInt(num, 10)
  return isNaN(parsed) ? '' : String(parsed)
}

// ── Constants ─────────────────────────────────────────────────────────────────

const statusOptions = ['Open', 'In Progress', 'On Hold', 'Closed', 'Completed']

const defaultTask: TaskFormData = {
  taskId: '',
  mainTaskId: '',
  taskName: '',
  taskDescription: '',
  taskTaken: '',
  taskGivenTo: '',
  taskGivenBy: '',
  status: 'Open',
  anyBlocker: '',
  worklogHour: 0,
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 7*86400000).toISOString().split('T')[0],
  deliveryUnit: 'Backend',
  priority: 'medium',
  estimatedHours: 0,
  actualHours: 0,
  totalCall: 0
}

// ── Styles ─────────────────────────────────────────────────────────────────

const popupStyles = `
  @keyframes modalFadeIn {
    from { opacity: 0; backdrop-filter: blur(0); }
    to { opacity: 1; backdrop-filter: blur(8px); }
  }
  @keyframes modalSlideIn {
    from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes modalShimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .tm-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(4, 17, 26, 0.85);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: modalFadeIn 0.25s ease both;
  }

  .tm-modal-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 85%;
    max-width: 650px;
    max-height: 85vh;
    overflow-y: auto;
    background: linear-gradient(160deg, #0e1c2a 0%, #0a1a28 100%);
    border: 1px solid rgba(0, 210, 200, 0.3);
    border-radius: 20px;
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(0, 210, 200, 0.2) inset;
    animation: modalSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .tm-modal-shimmer {
    height: 3px;
    background: linear-gradient(90deg, transparent, #00d2c8, #48cae4, #00d2c8, transparent);
    background-size: 200% 100%;
    animation: modalShimmer 3s linear infinite;
    border-radius: 20px 20px 0 0;
  }

  .tm-modal-header {
    padding: 16px 24px;
    background: linear-gradient(90deg, rgba(0, 160, 155, 0.15) 0%, rgba(0, 120, 175, 0.1) 100%);
    border-bottom: 1px solid rgba(0, 210, 200, 0.2);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .tm-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #e8f4f6;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .tm-modal-title-icon {
    font-size: 24px;
    filter: drop-shadow(0 0 6px rgba(0, 210, 200, 0.4));
  }

  .tm-modal-close {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: rgba(0, 210, 200, 0.1);
    border: 1px solid rgba(0, 210, 200, 0.25);
    color: #7ab0b8;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .tm-modal-close:hover {
    background: rgba(0, 210, 200, 0.2);
    color: #00d2c8;
    transform: rotate(90deg);
  }

  .tm-modal-body {
    padding: 20px 24px;
    background: #c5d7d5;
  }

  .tm-modal-section {
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.3));
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 16px;
    border: 1px solid rgba(0, 100, 95, 0.25);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .tm-modal-section-title {
    font-size: 12px;
    font-weight: 700;
    color: #1a5558;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 100, 95, 0.25);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .tm-modal-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  .tm-modal-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }

  .tm-modal-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }

  .tm-modal-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tm-modal-field-full {
    grid-column: 1 / -1;
  }

  .tm-modal-label {
    font-size: 11px;
    font-weight: 700;
    color: #1a5558;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .tm-modal-label-required {
    color: #c03030;
    font-size: 12px;
  }

  .tm-modal-input {
    width: 100%;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 100, 95, 0.25);
    border-radius: 10px;
    font-size: 12px;
    color: #0b2d30;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    transition: all 0.2s;
    outline: none;
  }

  .tm-modal-input:focus {
    border-color: #007a74;
    box-shadow: 0 0 0 3px rgba(0, 120, 115, 0.2);
    background: rgba(255, 255, 255, 0.95);
  }

  .tm-modal-input:hover:not(:focus) {
    border-color: rgba(0, 100, 95, 0.4);
    background: rgba(255, 255, 255, 0.9);
  }

  .tm-modal-input:disabled {
    background: rgba(200, 200, 200, 0.3);
    border-color: rgba(0, 100, 95, 0.15);
    color: #2a7070;
    cursor: not-allowed;
  }

  textarea.tm-modal-input {
    min-height: 60px;
    resize: vertical;
  }

  .tm-modal-select {
    width: 100%;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 100, 95, 0.25);
    border-radius: 10px;
    font-size: 12px;
    color: #0b2d30;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23007a74' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 14px;
  }

  .tm-modal-select:disabled {
    background: rgba(200, 200, 200, 0.3);
    border-color: rgba(0, 100, 95, 0.15);
    color: #2a7070;
    cursor: not-allowed;
  }

  .tm-modal-select option {
    background: #ddecea;
    color: #0b2d30;
    padding: 6px;
  }

  .tm-modal-footer {
    padding: 16px 24px;
    background: rgba(0, 0, 0, 0.3);
    border-top: 1px solid rgba(0, 210, 200, 0.2);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  .tm-modal-btn {
    padding: 8px 20px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    font-family: 'DM Sans', sans-serif;
  }

  .tm-modal-btn-primary {
    background: linear-gradient(135deg, #007a74, #005f6b);
    color: white;
    box-shadow: 0 2px 10px rgba(0, 122, 116, 0.4);
  }

  .tm-modal-btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #008a84, #006f7b);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 122, 116, 0.5);
  }

  .tm-modal-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .tm-modal-btn-secondary {
    background: rgba(0, 210, 200, 0.1);
    border: 1px solid rgba(0, 210, 200, 0.3);
    color: #7ab0b8;
  }

  .tm-modal-btn-secondary:hover:not(:disabled) {
    background: rgba(0, 210, 200, 0.18);
    color: #00d2c8;
  }

  .tm-modal-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .tm-modal-btn-danger {
    background: rgba(192, 48, 48, 0.15);
    border: 1px solid rgba(192, 48, 48, 0.3);
    color: #c03030;
  }

  .tm-modal-btn-danger:hover:not(:disabled) {
    background: rgba(192, 48, 48, 0.25);
  }

  .tm-modal-btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .tm-modal-error {
    background: rgba(192, 48, 48, 0.15);
    border: 1px solid rgba(192, 48, 48, 0.4);
    border-radius: 10px;
    padding: 10px 14px;
    color: #c03030;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tm-modal-success {
    background: rgba(0, 160, 140, 0.15);
    border: 1px solid rgba(0, 160, 140, 0.4);
    border-radius: 10px;
    padding: 10px 14px;
    color: #004a46;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tm-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 25px;
    font-size: 11px;
    font-weight: 700;
  }

  .tm-status-open { background: rgba(0, 160, 140, 0.15); color: #004a46; border: 1px solid rgba(0, 160, 140, 0.3); }
  .tm-status-progress { background: rgba(180, 120, 0, 0.15); color: #6a3c00; border: 1px solid rgba(180, 120, 0, 0.3); }
  .tm-status-hold { background: rgba(180, 40, 40, 0.15); color: #7a1010; border: 1px solid rgba(180, 40, 40, 0.3); }
  .tm-status-closed { background: rgba(80, 80, 80, 0.15); color: #444; border: 1px solid rgba(80, 80, 80, 0.3); }
  .tm-status-completed { background: rgba(0, 150, 140, 0.15); color: #004a46; border: 1px solid rgba(0, 150, 140, 0.3); }

  .tm-modal-container::-webkit-scrollbar {
    width: 6px;
  }

  .tm-modal-container::-webkit-scrollbar-track {
    background: rgba(0, 100, 95, 0.1);
    border-radius: 8px;
  }

  .tm-modal-container::-webkit-scrollbar-thumb {
    background: rgba(0, 210, 200, 0.3);
    border-radius: 8px;
  }

  .tm-modal-container::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 210, 200, 0.5);
  }

  .tm-employee-count-badge {
    font-size: 10px;
    color: #1a5558;
    margin-left: 6px;
    font-weight: normal;
  }

  .tm-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }

  @media (max-width: 768px) {
    .tm-modal-container {
      max-width: 90%;
      width: 90%;
    }
    
    .tm-modal-grid,
    .tm-modal-grid-3,
    .tm-modal-grid-4 {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  }
`

// ── Component ─────────────────────────────────────────────────────────────────

const TaskPopup: React.FC<TaskPopupProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  task, 
  mode,
  loggedInEmployeeCode,
  employees = [],
  isSubmitting = false,
  submitError = null
}) => {
  const [formData, setFormData] = useState<TaskFormData>(defaultTask)
  const [displayTaskId, setDisplayTaskId] = useState<string>('')
  const [displayMainTaskId, setDisplayMainTaskId] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens/closes or mode/task changes
  useEffect(() => {
    if (isOpen) {
      if (task && (mode === 'edit' || mode === 'view')) {
        const formattedTaskId = formatTaskIdForDisplay(task.taskId)
        const formattedMainTaskId = formatMainTaskIdForDisplay(task.mainTaskId || '')
        
        setDisplayTaskId(formattedTaskId)
        setDisplayMainTaskId(formattedMainTaskId)
        
        // Format dates properly for input fields (YYYY-MM-DD)
        let formattedStartDate = task.startDate || ''
        let formattedEndDate = task.endDate || ''
        
        // If startDate exists and is in DD/MM/YYYY format (from API), convert to YYYY-MM-DD
        if (formattedStartDate && formattedStartDate.includes('/')) {
          const parts = formattedStartDate.split('/')
          if (parts.length === 3) {
            formattedStartDate = `${parts[2]}-${parts[1]}-${parts[0]}`
          }
        }
        
        if (formattedEndDate && formattedEndDate.includes('/')) {
          const parts = formattedEndDate.split('/')
          if (parts.length === 3) {
            formattedEndDate = `${parts[2]}-${parts[1]}-${parts[0]}`
          }
        }
        
        setFormData({
          taskId: task.taskId,
          mainTaskId: task.mainTaskId || '',
          taskName: task.task,
          taskDescription: task.taskDescription || '',  // ✅ FIXED: Use taskDescription instead of taskTaken
          taskTaken: task.taskTaken || '',
          taskGivenTo: task.taskGivenTo || '',
          taskGivenBy: task.taskGivenBy || '',
          status: task.status,
          anyBlocker: task.anyBlocker || '',
          worklogHour: task.worklogHour || 0,
          startDate: formattedStartDate || new Date().toISOString().split('T')[0],
          endDate: formattedEndDate || new Date(Date.now() + 7*86400000).toISOString().split('T')[0],
          deliveryUnit: task.deliveryUnit || 'Backend',
          priority: task.priority || 'medium',
          estimatedHours: task.estimatedHours || 0,
          actualHours: task.actualHours || 0,
          totalCall: task.totalCall || 0,
          tags: task.tags || []
        })
      } else if (mode === 'create') {
        setDisplayTaskId('')
        setDisplayMainTaskId('')
        
        setFormData({
          ...defaultTask,
          taskId: '',
          mainTaskId: '',
          taskGivenBy: loggedInEmployeeCode || '',
          taskGivenTo: loggedInEmployeeCode || '',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 7*86400000).toISOString().split('T')[0],
          totalCall: 0
        })
      }
      
      setErrors({})
    }
  }, [isOpen, task, mode, loggedInEmployeeCode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    
    if (name === 'displayTaskId') {
      setDisplayTaskId(value)
      const parsedValue = parseTaskIdFromDisplay(value)
      setFormData(prev => ({ ...prev, taskId: parsedValue }))
    } else if (name === 'displayMainTaskId') {
      setDisplayMainTaskId(value)
      const parsedValue = parseMainTaskIdFromDisplay(value)
      setFormData(prev => ({ ...prev, mainTaskId: parsedValue }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const numValue = parseFloat(value) || 0
    setFormData(prev => ({ ...prev, [name]: numValue }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    
    if (mode === 'create') {
      if (!formData.taskName.trim()) newErrors.taskName = 'Task name is required'
    } else {
      if (!formData.taskId) newErrors.taskId = 'Task ID is required'
      if (!formData.taskName.trim()) newErrors.taskName = 'Task name is required'
    }
    
    if (!formData.taskDescription.trim()) newErrors.taskDescription = 'Task description is required'
    if (!formData.taskGivenTo) newErrors.taskGivenTo = 'Assignee is required'
    if (!formData.taskGivenBy) newErrors.taskGivenBy = 'Assigner is required'
    if (!formData.startDate) newErrors.startDate = 'Start date is required'
    if (!formData.endDate) newErrors.endDate = 'End date is required'
    
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'End date must be after start date'
    }
    
    if (formData.worklogHour < 0) {
      newErrors.worklogHour = 'Worklog hours cannot be negative'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (mode === 'view') {
      onClose()
      return
    }
    
    if (validateForm() && !isSubmitting) {
      const submitData = {
        ...formData,
        taskId: mode === 'create' ? '' : formData.taskId,
        mainTaskId: mode === 'create' ? '' : formData.mainTaskId,
      }
      onSave(submitData)
    }
  }

  const getEmployeeDisplay = (employeeCode: string) => {
    if (!employeeCode) return '—'
    const emp = employees.find(e => e.EmployeeCode === employeeCode)
    return emp ? `${emp.EmployeeName} (${emp.EmployeeCode})` : employeeCode
  }

  const getEmployeeOptions = () => {
    if (employees.length === 0) {
      return <option value="">No employees available</option>
    }
    
    return employees
      .filter(emp => emp.IsActive !== false)
      .map(emp => (
        <option key={emp.EmployeeCode} value={emp.EmployeeCode}>
          {emp.EmployeeName} ({emp.EmployeeCode}) - {emp.Designation}
        </option>
      ))
  }

  if (!isOpen) return null

  const isViewMode = mode === 'view'
  const title = mode === 'create' ? 'Create New Task' : mode === 'edit' ? 'Edit Task' : 'Task Details'
  const titleIcon = mode === 'create' ? '✨' : mode === 'edit' ? '📝' : '🔍'

  return (
    <>
      <style>{popupStyles}</style>
      <div className="tm-modal-overlay" onClick={onClose}>
        <div className="tm-modal-container" onClick={e => e.stopPropagation()}>
          <div className="tm-modal-shimmer" />
          
          <div className="tm-modal-header">
            <div className="tm-modal-title">
              <span className="tm-modal-title-icon">{titleIcon}</span>
              {title}
              {employees.length > 0 && (
                <span className="tm-employee-count-badge">
                  ({employees.length} employees)
                </span>
              )}
            </div>
            <div className="tm-modal-close" onClick={onClose}>✕</div>
          </div>

          <div className="tm-modal-body">
            {Object.keys(errors).length > 0 && (
              <div className="tm-modal-error">
                <span>⚠️</span>
                Please fix the errors before saving
              </div>
            )}

            {submitError && (
              <div className="tm-modal-error">
                <span>⚠️</span>
                {submitError}
              </div>
            )}

            {/* Task Identification Section */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <span>🆔</span> Task Identification
              </div>
              
              <div className="tm-modal-grid-3">
                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Task ID {mode !== 'create' && <span className="tm-modal-label-required">*</span>}
                    {mode === 'create' && <span style={{fontSize:'10px', marginLeft:'8px'}}>(Auto-generated)</span>}
                  </label>
                  {mode === 'create' ? (
                    <input
                      type="text"
                      name="displayTaskId"
                      value={displayTaskId}
                      onChange={handleChange}
                      className="tm-modal-input"
                      placeholder="TK-0001 (Auto)"
                      disabled={true}
                    />
                  ) : (
                    <input
                      type="text"
                      name="displayTaskId"
                      value={displayTaskId}
                      onChange={handleChange}
                      className="tm-modal-input"
                      placeholder="e.g., TK-0001"
                      disabled={isViewMode || isSubmitting}
                    />
                  )}
                  {errors.taskId && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.taskId}</span>}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Main Task ID
                  </label>
                  <input
                    type="text"
                    name="displayMainTaskId"
                    value={displayMainTaskId}
                    onChange={handleChange}
                    className="tm-modal-input"
                    placeholder="e.g., MT-0001"
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.mainTaskId && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.mainTaskId}</span>}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Task Name <span className="tm-modal-label-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="taskName"
                    value={formData.taskName}
                    onChange={handleChange}
                    className="tm-modal-input"
                    placeholder="Enter task name"
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.taskName && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.taskName}</span>}
                </div>
              </div>
            </div>

            {/* Task Description Section */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <span>📝</span> Task Details
              </div>
              
              <div className="tm-modal-field tm-modal-field-full">
                <label className="tm-modal-label">
                  Task Description <span className="tm-modal-label-required">*</span>
                </label>
                <textarea
                  name="taskDescription"
                  value={formData.taskDescription}
                  onChange={handleChange}
                  className="tm-modal-input"
                  placeholder="Describe the task in detail..."
                  disabled={isViewMode || isSubmitting}
                  rows={3}
                />
                {errors.taskDescription && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.taskDescription}</span>}
              </div>
            </div>

            {/* Assignment Section */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <span>👥</span> Assignment
              </div>

              <div className="tm-modal-grid">
                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Task Given To <span className="tm-modal-label-required">*</span>
                  </label>
                  {isViewMode ? (
                    <div className="tm-modal-input" style={{background: 'rgba(200,200,200,0.3)'}}>
                      {getEmployeeDisplay(formData.taskGivenTo)}
                    </div>
                  ) : (
                    <select 
                      name="taskGivenTo" 
                      value={formData.taskGivenTo}
                      onChange={handleChange}
                      className="tm-modal-select"
                      disabled={isSubmitting}
                    >
                      <option value="">Select Employee</option>
                      {getEmployeeOptions()}
                    </select>
                  )}
                  {errors.taskGivenTo && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.taskGivenTo}</span>}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Task Given By <span className="tm-modal-label-required">*</span>
                  </label>
                  {isViewMode ? (
                    <div className="tm-modal-input" style={{background: 'rgba(200,200,200,0.3)'}}>
                      {getEmployeeDisplay(formData.taskGivenBy)}
                    </div>
                  ) : (
                    <select 
                      name="taskGivenBy" 
                      value={formData.taskGivenBy}
                      onChange={handleChange}
                      className="tm-modal-select"
                      disabled={isSubmitting}
                    >
                      <option value="">Select Employee</option>
                      {getEmployeeOptions()}
                    </select>
                  )}
                  {errors.taskGivenBy && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.taskGivenBy}</span>}
                </div>
              </div>
            </div>

            {/* Status & Dates Section */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <span>📊</span> Status & Timeline
              </div>

              <div className="tm-modal-grid-4">
                <div className="tm-modal-field">
                  <label className="tm-modal-label">Status</label>
                  {isViewMode ? (
                    <div className={`tm-status-badge tm-status-${formData.status.toLowerCase().replace(' ', '')}`}>
                      {formData.status}
                    </div>
                  ) : (
                    <select 
                      name="status" 
                      value={formData.status}
                      onChange={handleChange}
                      className="tm-modal-select"
                      disabled={isSubmitting}
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    Start Date <span className="tm-modal-label-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="tm-modal-input"
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.startDate && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.startDate}</span>}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">
                    End Date <span className="tm-modal-label-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="tm-modal-input"
                    disabled={isViewMode || isSubmitting}
                    min={formData.startDate}
                  />
                  {errors.endDate && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.endDate}</span>}
                </div>

                <div className="tm-modal-field">
                  <label className="tm-modal-label">Total Call</label>
                  <input
                    type="number"
                    name="totalCall"
                    value={formData.totalCall}
                    onChange={handleNumberChange}
                    className="tm-modal-input"
                    min="0"
                    step="1"
                    disabled={isViewMode || isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Worklog & Blocker Section */}
            <div className="tm-modal-section">
              <div className="tm-modal-section-title">
                <span>⚙️</span> Worklog & Issues
              </div>

              <div className="tm-modal-grid">
                <div className="tm-modal-field">
                  <label className="tm-modal-label">Worklog Hours</label>
                  <input
                    type="number"
                    name="worklogHour"
                    value={formData.worklogHour}
                    onChange={handleNumberChange}
                    className="tm-modal-input"
                    min="0"
                    step="0.5"
                    disabled={isViewMode || isSubmitting}
                  />
                  {errors.worklogHour && <span style={{color:'#c03030', fontSize:'11px'}}>{errors.worklogHour}</span>}
                </div>

                <div className="tm-modal-field tm-modal-field-full">
                  <label className="tm-modal-label">Any Blocker (Describe If Any)</label>
                  <textarea
                    name="anyBlocker"
                    value={formData.anyBlocker}
                    onChange={handleChange}
                    className="tm-modal-input"
                    placeholder="Describe any blockers or issues..."
                    disabled={isViewMode || isSubmitting}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="tm-modal-footer">
            {mode !== 'create' && onDelete && mode !== 'view' && (
              <button 
                className="tm-modal-btn tm-modal-btn-danger"
                onClick={onDelete}
                disabled={isSubmitting}
              >
                🗑️ Delete Task
              </button>
            )}
            
            <button 
              className="tm-modal-btn tm-modal-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            
            <button 
              className="tm-modal-btn tm-modal-btn-primary"
              onClick={handleSubmit}
              disabled={isViewMode || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="tm-spinner" />
                  {mode === 'create' ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  {mode === 'create' && '✨ Create Task'}
                  {mode === 'edit' && '💾 Save Changes'}
                  {mode === 'view' && '✓ Close'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default TaskPopup